import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useTranslation } from 'react-i18next'; 

const LoadingState: React.FC = () => {
  const { t } = useTranslation();

  // Smooth, semi-random progress that asymptotically approaches ~98%
  const [progress, setProgress] = useState(8);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    // Clear any running interval before creating a new one
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
    }

    intervalRef.current = window.setInterval(() => {
      setProgress((current) => {
        // Add small, jittery increments that slow down as we get closer to 100
        let increment = 0;
        if (current < 40) {
          increment = 2 + Math.random() * 2; // 2-4
        } else if (current < 75) {
          increment = 1 + Math.random() * 1.5; // 1-2.5
        } else if (current < 90) {
          increment = 0.5 + Math.random() * 0.8; // 0.5-1.3
        } else if (current < 97) {
          increment = 0.2 + Math.random() * 0.4; // 0.2-0.6
        } else {
          increment = 0.05; // crawl but never reach 100
        }

        // Occasionally pause to feel more realistic
        const shouldPause = Math.random() < 0.08;
        if (shouldPause) return current;

        const next = Math.min(98, current + increment);
        return next;
      });
    }, 140);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <Card className="shadow-elegant bg-white/95 backdrop-blur-sm">
      <CardContent className="py-12">
        <div className="text-center space-y-4">
          <div className="animate-spin mx-auto w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
          <div>
            <h3 className="text-lg font-semibold mb-2">{t('results.loading.title')}</h3>
            <p className="text-muted-foreground">
              {t('results.loading.subtitle')}
            </p>
          </div>
          <Progress value={progress} className="w-64 mx-auto" />
        </div>
      </CardContent>
    </Card>
  );
};

export default LoadingState; 